import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, CheckCircle2, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";

export default function Tally() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { category: "tally-education", limit: 10 } })
      .then((r) => setCourses(r.data.items)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="tally-page">
      <SEO title="Tally Course in Jorhat — TallyPrime with GST | XTM TECHNOLOGIES"
        description="TallyPrime with GST, financial accounting, payroll and inventory training in Jorhat, plus TCAT preparation at XTM FutureSkills Academy." />
      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Tally Education</div>
          <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-300 mb-4" data-testid="tally-authorized-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Authorised Training Center of Tally Education
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="tally-heading">
            Accounting Skills That <span className="text-gradient-orange">Get You Hired</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">
            TallyPrime, GST, payroll, inventory and business accounting — practical training for commerce and non-commerce students in Jorhat.
          </p>
          <EnquiryDialog type="counselling" title="Tally Course Enquiry" trigger={
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 transition-colors" data-testid="tally-enquire-btn">Enquire Now</Button>
          } />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-testid="tally-courses-grid">
          {courses.map((c) => <CourseCard key={c.slug} course={c} />)}
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-8 glow-card" data-testid="tcat-section">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="w-7 h-7 text-orange-400" />
            <h2 className="font-display text-2xl font-bold text-white">Tally Commerce Aptitude Test (TCAT)</h2>
          </div>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">
            TCAT is an MCQ-based online assessment measuring accounting aptitude. Duration, number of questions, eligibility, registration, test schedule and result publication are configured by the XTM administration for each test cycle.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {["Accounting & Tally Basics", "Office Automation", "Basic English"].map((t) => (
              <div key={t} className="bg-navy-deep border border-white/10 rounded-xl p-4 text-sm font-medium text-white flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{t}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <EnquiryDialog type="enquiry" course="TCAT Registration" title="Register Interest for TCAT" trigger={
              <Button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-7 transition-colors" data-testid="tcat-register-btn">Register Interest</Button>
            } />
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-7 transition-colors" data-testid="tally-course-btn">
              <Link to="/courses/category/tally-education"><Calculator className="w-4 h-4 mr-2" /> View Tally Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
